/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, Stack, Typography, Grid, useMediaQuery, Divider, Button, Tooltip } from '@mui/material';
import { Loader } from 'app/components';
import {
  Container,
  QuickArea,
  NewsFeedGrid,
  TopHeadingGrid,
  MessageArea,
  CertFeedGrid,
} from './style';
import { setChanges } from 'app/store/features/privacyPersistedSlice';
import { deletePolicyByCache } from 'app/store/features/privacySlice';
import { setAlertOpen } from 'app/store/features/toggleSidebarSlice';
import { ColorsV2 } from 'app/constants/color';
import { WIDGETS_INITIAL_STATE } from 'app/utils/constants';
import apiService from 'app/services/apiService';
import CardItem from './Card';
import CertFeed from './CertSeFeed';
import EnisaNews from './EnisaNews';
import NewsFeed from './NewsFeed';
import QuickGuide from './QuickGuide';
import WidgetsPopup from './WidgetsModal';
import { fetchUserSettings } from 'app/store/features/userSettingsSlice';
import { updateUserSettings } from 'app/store/features/userSettingsSlice';
import { Buttons } from 'app/constants/sizing';
import Cookies from 'universal-cookie';
import { routes } from 'app/routes';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [promiseLoading, setPromiseLoading] = useState(false);
  const [structuredAndUnstructuredData, setStructuredAndUnstructuredData] = useState({
    structured: null,
    unstructured: null,
  });

  const { t, i18n } = useTranslation('translation', { keyPrefix: 'dashboard_headings' });

  const widgets = useSelector(state => state.userSettings.settings?.selected) || {};
  const widgetsLoading = useSelector((state) => state.userSettings.loading);
  const { createdPolicy, changes } = useSelector(state => state.privacyPersisted);
  const accountSettings = useSelector(state => state.updateUser.userData);

  const [selected, setSelected] = useState(WIDGETS_INITIAL_STATE);
  const [initial, setInitial] = useState(WIDGETS_INITIAL_STATE);
  const [versionInfo, setVersionInfo] = useState({});

  const allWidgetsHidden = Object.values(selected).every(item => !item.visibility);

  const togglePopup = () => {
    setIsOpen(prev => !prev);
  };

  const fetchVersionInfo = async () => {
    try {
      const res = await apiService.fetchVersionInfo();
      setVersionInfo(res.data.data);
    }
    catch (error) {
      console.error(error);
    }
  }

  const fetchData = async () => {
    try {
      setPromiseLoading(true);

      // eslint-disable-next-line no-undef
      const [structuredRes, unstructuredRes] = await Promise.all([
        apiService.structuredChartData('/structured'),
        apiService.structuredChartData('/unstructured'),
      ]);

      setStructuredAndUnstructuredData({
        structured: structuredRes?.data?.structured,
        unstructured: unstructuredRes?.data?.unstructured,
      });
      setPromiseLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const cookies = new Cookies();
    const osmcookie = cookies.get('nic-osm');
    if(osmcookie){
      navigate(routes.ORGSELECT);
      return;
    }
    fetchData();
    fetchVersionInfo();
    dispatch(fetchUserSettings());
    dispatch(setChanges(false));
    dispatch(setAlertOpen(false));
    if (createdPolicy?.policyType && changes) {
      dispatch(deletePolicyByCache(createdPolicy?.policyType));
    }
  }, []);

  useEffect(() => {
    if (typeof widgets !== 'object') { // If widgets is not an object i.e is of a faulty structure: reset user widget settings
      setSelected(WIDGETS_INITIAL_STATE);
      dispatch(updateUserSettings({ key: 'selected', value: WIDGETS_INITIAL_STATE }));
    }
    else if (Object.keys(widgets)?.length > 0) {
      setSelected(widgets);
      setInitial(widgets);
    }
  }, [widgets]);

  const matches = useMediaQuery('(min-width:1280px)');
  const matchesMac = useMediaQuery('(max-width:1440px)');
  const matchesDesktop = useMediaQuery('(max-width:1600px)');

  if (widgetsLoading) {
    return <Loader color={ColorsV2.acent} />;
  }

  return (
      <Container>
        <TopHeadingGrid item xs={12} md={12}>
          <Box sx={{ marginTop: matchesDesktop ? '1vh' : '' }}>
            <Stack direction='row' spacing={1}>
              <Typography
                variant='h5'
                noWrap
                component='div'
                color={ColorsV2.white}
                fontSize={'1.2em'}
              >
                {i18n.t('dashboard.welcomePhrase')} -
              </Typography>
              <Typography
                variant='h5'
                noWrap
                component='div'
                color={ColorsV2.acent}
                fontSize={'1.2em'}
              >
                {`${accountSettings?.firstName || ''} ${accountSettings?.lastName || ''}`}
              </Typography>
            </Stack>
            <Typography
              variant='h4'
              noWrap
              component='div'
              color={ColorsV2.white}
              fontSize={'1.9em'}
              fontWeight={500}
              gutterBottom
            >
              {i18n.t('dashboard.welcome')}
            </Typography>
          </Box>
          <Button
            name={'IconButton'}
            onClick={togglePopup}
            startIcon={<SettingsOutlinedIcon sx={{ fontSize: '0.9em' }} fill={ColorsV2.white} />}
            sx={{
              ...Buttons.baseStyle.primaryButtonLowPriority,
              height: '40px'
            }}
          >
            {t('Settings')}
          </Button>
        </TopHeadingGrid>
        {allWidgetsHidden ? (
          <MessageArea
            variant='div'
            align='center'
            style={{
              marginTop: '100px',
              fontSize: '1.2em',
              lineHeight: '35px',
              color: ColorsV2.white,
              border: `1px solid ${ColorsV2.outline}`,
            }}
          >
            {t('NoWidgets')}
          </MessageArea>
        ) : (
          <Grid container mx={1.7} my={1}>
            <Grid container item mb={4} mr={3}>
              {selected['dataSources']?.visibility && (
                <Grid item xs={10} md={2.85} lg={2.85} mr={2.5}>
                  <CardItem
                    data={
                      (structuredAndUnstructuredData?.structured?.datasources?.length || 0) +
                      (structuredAndUnstructuredData?.unstructured?.datasources?.length || 0)
                    }
                    loading={promiseLoading}
                    icon={<FolderOpenIcon sx={{ fontSize: '1.5em', color: ColorsV2.primary }} />}
                    title={i18n.t('data_sources.Data Sources')}
                  />
                </Grid>
              )}
              {selected['dataScanning']?.visibility && (
                <Grid item xs={10} md={2.85} lg={2.85}>
                  <CardItem
                    data={
                      structuredAndUnstructuredData?.structured?.nrOfScannedObjects +
                      structuredAndUnstructuredData?.unstructured?.nrOfScannedObjects
                    }
                    loading={promiseLoading}
                    icon={
                      <EmojiObjectsOutlinedIcon
                        sx={{ fontSize: '1.7em', color: ColorsV2.primary }}
                      />
                    }
                    title={i18n.t('sources_scanned.Data Sources Scanned')}
                  />
                </Grid>
              )}

              {selected['officeInfo']?.visibility && (
                <Grid item xs={10} xl={2.85} md={4} lg={3.85} ml={'auto'}>
                  <CardItem
                    data={'tech.support@nord-ic.com'}
                    loading={false}
                    icon={
                      <Tooltip title={
                        <Typography variant='caption' sx={{ whiteSpace: 'pre-line' }}>
                          {i18n.t('dashboard.version_info')}<br />
                          {
                            Object.entries(versionInfo || {})
                              .map(([key, value]) => `${key}: ${value}`)
                              .join('\n')
                          }
                        </Typography>
                      }><SupportAgentIcon color={ColorsV2.primary} /></Tooltip>
                        }
                    title={'+46 8 502 350 30'}
                  />
                </Grid>
              )}
            </Grid>
            <Divider
              sx={{
                background: ColorsV2.acent,
                height: '0.2vh',
                width: '100%',
                marginBottom: '20px',
              }}
            />

            <Grid container mb={0} mx={0} my={1}>
              <Grid
                item
                xs={12}
                lg={5.8}
                mr={matchesMac ? 2 : matchesDesktop ? 2 : matches ? 2.5 : 4}
                sx={{ overflow: 'hidden' }}
              >
                {selected['quickGuide']?.visibility && (
                  <QuickArea item xs={12} lg={12}>
                    <QuickGuide />
                  </QuickArea>
                )}
                {selected['certFeeds']?.visibility && (
                  <CertFeedGrid item xs={12} lg={12} mt={2} sx={{ overflow: 'hidden' }}>
                    <Typography
                      sx={{
                        backgroundColor: ColorsV2.primary,
                        color: ColorsV2.white,
                        fontSize: '0.8em',
                        fontWeight: 500,
                        paddingX: '20px',
                        paddingY: '15px',
                        height: '5%',
                      }}
                    >
                      Cert Feed
                    </Typography>
                    <div
                      style={{
                        height: 'calc(95% - 25px)',
                        width: '100%',
                        overflow: 'scroll',
                        position: 'absolute',
                      }}
                    >
                      <CertFeed />
                    </div>
                  </CertFeedGrid>
                )}
              </Grid>
              <Grid item xs={12} lg={5.8}>
                {selected['newsFeeds']?.visibility && (
                  <NewsFeedGrid item xs={12} lg={12} mb={2} sx={{ overflow: 'hidden' }}>
                    <Typography
                      sx={{
                        backgroundColor: ColorsV2.primary,
                        color: ColorsV2.white,
                        fontSize: '0.8em',
                        fontWeight: 500,
                        paddingX: '20px',
                        paddingY: '15px',
                        height: '5%',
                      }}
                    >
                      {t('News Feed RSS')}
                    </Typography>
                    <div
                      style={{
                        height: 'calc(95% - 25px)',
                        width: '100%',
                        overflow: 'scroll',
                        position: 'absolute',
                      }}
                    >
                      <NewsFeed />
                    </div>
                  </NewsFeedGrid>
                )}

                {selected['enisaNews']?.visibility && (
                  <NewsFeedGrid item xs={12} lg={12} mb={2} sx={{ overflow: 'hidden' }}>
                    <Typography
                      sx={{
                        backgroundColor: ColorsV2.primary,
                        color: ColorsV2.white,
                        fontSize: '0.8em',
                        fontWeight: 500,
                        paddingX: '20px',
                        paddingY: '15px',
                        height: '5%',
                      }}
                    >
                      {'Enisa News'}
                    </Typography>
                    <div
                      style={{
                        height: 'calc(95% - 25px)',
                        width: '100%',
                        overflow: 'scroll',
                        position: 'absolute',
                      }}
                    >
                      <EnisaNews />
                    </div>
                  </NewsFeedGrid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
        {isOpen && (
          <WidgetsPopup
            open={isOpen}
            setIsOpen={setIsOpen}
            selected={selected}
            setSelected={setSelected}
            initial={initial}
          />
        )}
      </Container>
  );
};

export default Home;
